import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, X, Gavel } from "@phosphor-icons/react";

export default function Committee() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/committee-decisions");
      setRows(r.data);
    } catch { toast.error("Failed to load decisions"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="committee-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Medical Staff</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Committee decisions</h1>
          <p className="text-sm text-gray-600 mt-2">
            Record initial appointments, re-credentialing approvals, and provisional actions. 36-month cycle per LA Medicaid.
          </p>
        </div>
        <button onClick={() => setShow(true)} className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700" data-testid="record-decision-btn">
          <Plus size={14} weight="bold" /> Record decision
        </button>
      </header>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="committee-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
              <th className="px-6 py-3 font-semibold">Practitioner</th>
              <th className="px-4 py-3 font-semibold">Decision type</th>
              <th className="px-4 py-3 font-semibold">Outcome</th>
              <th className="px-4 py-3 font-semibold">Decision date</th>
              <th className="px-4 py-3 font-semibold">Next review</th>
              <th className="px-4 py-3 font-semibold">Committee</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="6" className="p-12 text-center text-gray-500 text-sm">No committee decisions recorded.</td></tr>
            )}
            {rows.map((d) => (
              <tr key={d.id} className="data-row border-b border-gray-100">
                <td className="px-6 py-3 font-medium text-slate-900">
                  {d.practitioner?.last_name}, {d.practitioner?.first_name} <span className="text-xs text-gray-500 font-mono">({d.practitioner?.title})</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{d.decision_type}</td>
                <td className="px-4 py-3">
                  <span className={`status-pill ${d.decision === "APPROVE" ? "status-current" : d.decision === "DENY" ? "status-expired" : "status-exp-30"}`}>{d.decision}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{formatDate(d.decision_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{formatDate(d.next_review_date)}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{d.committee_members || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && <DecisionModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function DecisionModal({ onClose, onSaved }) {
  const [practitioners, setPractitioners] = useState([]);
  const [form, setForm] = useState({
    practitioner_id: "",
    decision_type: "INITIAL",
    decision: "APPROVE",
    decision_date: new Date().toISOString().slice(0, 10),
    next_review_date: "",
    committee_members: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/practitioners").then((r) => setPractitioners(r.data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.practitioner_id) return toast.error("Select a practitioner");
    setSaving(true);
    try {
      await api.post("/committee-decisions", form);
      toast.success("Decision recorded");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="decision-modal">
      <div className="bg-white border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 flex items-center justify-center"><Gavel size={16} weight="bold" className="text-white" /></div>
            <div>
              <div className="overline text-gray-500">New record</div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Committee decision</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-slate-900"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Practitioner *</label>
            <select value={form.practitioner_id} onChange={set("practitioner_id")} required className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="decision-practitioner-select">
              <option value="">— Select —</option>
              {practitioners.map((p) => <option key={p.id} value={p.id}>{p.last_name}, {p.first_name} ({p.title})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Type</label>
            <select value={form.decision_type} onChange={set("decision_type")} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="decision-type">
              <option value="INITIAL">Initial</option>
              <option value="RECREDENTIAL">Re-credentialing</option>
              <option value="PROVISIONAL">Provisional</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Outcome</label>
            <select value={form.decision} onChange={set("decision")} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="decision-outcome">
              <option value="APPROVE">Approve</option>
              <option value="DENY">Deny</option>
              <option value="DEFER">Defer</option>
            </select>
          </div>
          <Field label="Decision date *" type="date" value={form.decision_date} onChange={set("decision_date")} testid="decision-date" required />
          <Field label="Next review date" type="date" value={form.next_review_date} onChange={set("next_review_date")} testid="decision-next-review" />
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Committee members</label>
            <input value={form.committee_members} onChange={set("committee_members")} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="decision-members" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="decision-notes" />
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-60" data-testid="decision-save-btn">{saving ? "Saving…" : "Record decision"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type="text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">{label}</label>
      <input required={required} type={type} value={value} onChange={onChange} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid={testid} />
    </div>
  );
}
